// src/pages/Home.js
// ✅ Full Product Costing Page
// ✅ Add/Edit Product restructured into 3 sections
// ✅ Board Setup arranged in one full page
// ✅ Direct 3-box product finder: Category / Sub Category / Name
// ✅ Supports backend spellings: category/catageory and subCategory/subcatageory
// ✅ Product name click from Order Sheet opens product directly in edit mode
// ✅ All-field filters + Smart AI-style filter module also kept
// ✅ Adjustable field box width
// ✅ Board Setup GSM Group A+B used in Order Sheet GSM dropdown
// ✅ GST Amount = Amount * Paper GST %
// ✅ Total Amount = Amount + GST Amount
// ✅ Price / Piece = Total Amount / Total Pcs
// ✅ Total Cost = Total Amount + Printing + Plate + Cutting + Conversion + Misc
// ✅ Profit / Piece = Sale Price - Cost / Pcs
// ✅ Bulk upload keeps text fields as string

import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  TextField,
  Typography,
  CssBaseline,
  Container,
  Card,
  CardActionArea,
  CardContent,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Autocomplete,
  Slider,
} from "@mui/material";

import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from "@mui/icons-material/Description";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import AddIcon from "@mui/icons-material/Add";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import * as XLSX from "xlsx";
import { BaseUrl } from "../BaseUrl";

const API_URL = `${BaseUrl}/api/products`;
const BOARD_SETUP_URL = `${BaseUrl}/api/board-quality-configs`;

/* ------------------ FIELD GROUPS ------------------ */
const GROUPS = [
  {
    title: "Product Details",
    fields: [
      "category",
      "subCategory",
      "companyName",
      "boardQuality",
      "name",
      "lcm",
      "bcm",
      "wtPer100",
      "gsm",
      "bagQnty",
      "bagWeight",
      "salePrice",
    ],
    accentBg: "#EEF6FF",
    accentBorder: "#B8D8FF",
  },
  {
    title: "Order Details For Production",
    fields: [
      "pcsBreadth",
      "pcsLength",
      "piecePerSheet",
      "orderSheetB",
      "orderSheetL",
      "orderSheetGsm",
      "quantity",
      "totalPcs",
      "totalWeight",
      "rate",
      "amount",
      "gstAmount",
      "totalAmount",
      "pricePerPiece",
    ],
    accentBg: "#F2FFF5",
    accentBorder: "#BCE9C7",
  },
  {
    title: "Costing With Print",
    fields: [
      "printingCharges",
      "plateCharge",
      "cuttingCharges",
      "conversionCost",
      "miscCost",
      "totalCost",
      "costPerPcs",
      "profitPerPiece",
    ],
    accentBg: "#FFF6EC",
    accentBorder: "#FFD5A6",
  },
];

/* ------------------ ALL FIELDS ------------------ */
const fields = [
  "category",
  "subCategory",
  "companyName",
  "boardQuality",
  "name",
  "lcm",
  "bcm",
  "wtPer100",
  "gsm",
  "bagQnty",
  "bagWeight",
  "salePrice",

  "pcsBreadth",
  "pcsLength",
  "piecePerSheet",
  "orderSheetB",
  "orderSheetL",
  "orderSheetGsm",
  "quantity",
  "totalPcs",
  "totalWeight",
  "rate",
  "amount",
  "gstAmount",
  "totalAmount",
  "pricePerPiece",

  "printingCharges",
  "plateCharge",
  "cuttingCharges",
  "conversionCost",
  "miscCost",
  "totalCost",
  "costPerPcs",
  "profitPerPiece",
];

/* ------------------ AUTO CALCULATED FIELDS ------------------ */
const calculatedFields = [
  "gsm",
  "bagWeight",
  "piecePerSheet",
  "totalPcs",
  "totalWeight",
  "rate",
  "amount",
  "gstAmount",
  "totalAmount",
  "pricePerPiece",
  "printingCharges",
  "plateCharge",
  "cuttingCharges",
  "conversionCost",
  "miscCost",
  "totalCost",
  "costPerPcs",
  "profitPerPiece",
];

const UI = {
  pageBg: "#EEF1F4",
  headerBg: "#131921",
  amazonOrange: "#FF9900",
  amazonOrangeDeep: "#F08804",
  linkBlue: "#007185",
  cardBg: "#FFFFFF",
  text: "#111",
  sub: "#565959",
  border: "#D5D9D9",
  softPanel: "#F7F8FA",
};

/* ------------------ HELPERS ------------------ */
function labelize(field) {
  const map = {
    category: "Category",
    subCategory: "Sub Category",
    companyName: "Company Name",
    boardQuality: "Board Quality",
    name: "Name",
    lcm: "Lcm",
    bcm: "Bcm",
    wtPer100: "Wt Per 100",
    gsm: "Gsm",
    bagQnty: "Bag Qnty",
    bagWeight: "Bag Weight",
    salePrice: "Sale Price",

    pcsBreadth: "Pcs Breadth",
    pcsLength: "Pcs Length",
    piecePerSheet: "Piece Per Sheet",
    orderSheetB: "Order Sheet B",
    orderSheetL: "Order Sheet L",
    orderSheetGsm: "Order Sheet Gsm",
    quantity: "Quantity",
    totalPcs: "Total Pcs",
    totalWeight: "Total Weight",
    rate: "Rate",
    amount: "Amount",
    gstAmount: "GST Amount",
    totalAmount: "Total Amount",
    pricePerPiece: "Price / Piece",

    printingCharges: "Printing Charges",
    plateCharge: "Plate Charge",
    cuttingCharges: "Cutting Charges",
    conversionCost: "Conversion Cost",
    miscCost: "Misc Cost",
    totalCost: "Total Cost",
    costPerPcs: "Cost / Pcs",
    profitPerPiece: "Profit / Piece",
  };

  return (
    map[field] ||
    field.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase())
  );
}

function colorFromId(id = "") {
  const colors = [
    "#FF9900",
    "#F08804",
    "#00A8E1",
    "#146EB4",
    "#B12704",
    "#007600",
    "#8E44AD",
    "#D35400",
    "#16A085",
    "#2E86C1",
  ];

  let sum = 0;
  for (let i = 0; i < id.length; i++) sum += id.charCodeAt(i);

  return colors[sum % colors.length];
}

function norm(v) {
  return (v ?? "").toString().trim().toLowerCase();
}

function safeStr(v) {
  return (v ?? "").toString();
}

function toNum(v) {
  if (v === "" || v == null) return null;

  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

function headerKeyNorm(s) {
  return safeStr(s).trim().toLowerCase().replace(/\s+/g, "").replace(/_/g, "");
}

function fmt(v, d = 2) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n.toFixed(d) : "0.00";
}

/* ------------------ API NORMALIZATION ------------------ */
function normalizeProductFromApi(p) {
  const cat = (p?.category ?? "").toString().trim();
  const catTypo = (p?.catageory ?? "").toString().trim();

  const subCat =
    p?.subCategory ??
    p?.subcategory ??
    p?.subCatageory ??
    p?.subcatageory ??
    p?.sub_catageory ??
    "";

  return {
    ...p,

    category: cat || catTypo || "",
    subCategory: safeStr(subCat).trim(),

    gstAmount: p?.gstAmount ?? p?.gstRate ?? "",
    printingCharges: p?.printingCharges ?? p?.printingPer1000 ?? "",
    cuttingCharges: p?.cuttingCharges ?? p?.cuttingPer1000 ?? "",

    totalAmount: p?.totalAmount ?? "",
    pricePerPiece: p?.pricePerPiece ?? "",
    profitPerPiece: p?.profitPerPiece ?? "",
  };
}

function normalizePayloadForApi(p) {
  const payload = { ...(p || {}) };

  payload.category = payload.category ?? "";
  payload.catageory = payload.category;

  payload.subCategory = payload.subCategory ?? "";
  payload.subcatageory = payload.subCategory;
  payload.subCatageory = payload.subCategory;

  payload.gstRate = payload.gstAmount ?? payload.gstRate ?? "";
  payload.printingPer1000 =
    payload.printingCharges ?? payload.printingPer1000 ?? "";
  payload.cuttingPer1000 =
    payload.cuttingCharges ?? payload.cuttingPer1000 ?? "";

  return payload;
}

/* ------------------ LOCAL STORAGE ------------------ */
const LS_LAST_PRODUCT_KEY = "rupioo_costing_last_product_v2";
const LS_SUGGEST_KEY = "rupioo_costing_suggest_v2";
const LS_EDITOR_WIDTH_KEY = "rupioo_costing_editor_box_width_v1";
const OPEN_PRODUCT_EDIT_KEY = "rupioo_costing_open_product_edit_id";

function loadLS(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;

    const obj = JSON.parse(raw);
    return obj ?? fallback;
  } catch {
    return fallback;
  }
}

function saveLS(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value ?? null));
  } catch {}
}

function pushUniqueStr(list, value, limit = 25) {
  const v = (value ?? "").toString().trim();
  if (!v) return Array.isArray(list) ? list : [];

  const arr = Array.isArray(list) ? [...list] : [];

  const cleaned = arr.filter(
    (x) => (x ?? "").toString().trim().toLowerCase() !== v.toLowerCase(),
  );

  cleaned.unshift(v);
  return cleaned.slice(0, limit);
}

/* ======================= FILTER HELPERS ======================= */
const NUMERIC_FILTER_FIELDS = new Set(
  fields.filter(
    (f) =>
      ![
        "category",
        "subCategory",
        "companyName",
        "boardQuality",
        "name",
      ].includes(f),
  ),
);

const DEFAULT_FILTER_RULE = {
  field: "name",
  operator: "contains",
  value: "",
};

const OPERATOR_OPTIONS = [
  { value: "contains", label: "Contains" },
  { value: "equals", label: "Equals" },
  { value: "startsWith", label: "Starts With" },
  { value: "gt", label: "Greater Than" },
  { value: "gte", label: "Greater / Equal" },
  { value: "lt", label: "Less Than" },
  { value: "lte", label: "Less / Equal" },
];

function isNumericFilterField(field) {
  return NUMERIC_FILTER_FIELDS.has(field);
}

function applyFieldFilterRule(product, rule) {
  if (!rule?.field || !rule?.operator) return true;

  const filterValue = safeStr(rule.value).trim();
  if (!filterValue) return true;

  const rawValue = product?.[rule.field];

  if (["gt", "gte", "lt", "lte"].includes(rule.operator)) {
    const left = parseFloat(rawValue);
    const right = parseFloat(filterValue);

    if (!Number.isFinite(left) || !Number.isFinite(right)) return false;

    if (rule.operator === "gt") return left > right;
    if (rule.operator === "gte") return left >= right;
    if (rule.operator === "lt") return left < right;
    if (rule.operator === "lte") return left <= right;

    return true;
  }

  const leftText = norm(rawValue);
  const rightText = norm(filterValue);

  if (rule.operator === "contains") return leftText.includes(rightText);
  if (rule.operator === "equals") return leftText === rightText;
  if (rule.operator === "startsWith") return leftText.startsWith(rightText);

  return true;
}

function smartAliasToField(alias = "") {
  const a = norm(alias).replace(/\s+/g, "");

  const map = {
    category: "category",
    cat: "category",
    catageory: "category",

    subcategory: "subCategory",
    subcat: "subCategory",
    subcatageory: "subCategory",

    company: "companyName",
    companyname: "companyName",

    board: "boardQuality",
    boardquality: "boardQuality",
    quality: "boardQuality",

    name: "name",
    product: "name",
    productname: "name",

    gsm: "gsm",
    sale: "salePrice",
    saleprice: "salePrice",

    qty: "quantity",
    quantity: "quantity",

    rate: "rate",
    amount: "amount",

    gst: "gstAmount",
    gstamount: "gstAmount",

    total: "totalAmount",
    totalamount: "totalAmount",

    price: "pricePerPiece",
    pricepiece: "pricePerPiece",
    priceperpiece: "pricePerPiece",

    cost: "costPerPcs",
    costpiece: "costPerPcs",
    costperpcs: "costPerPcs",
    costperpiece: "costPerPcs",

    totalcost: "totalCost",

    profit: "profitPerPiece",
    profitpiece: "profitPerPiece",
    profitperpiece: "profitPerPiece",
  };

  return map[a] || "";
}

function applySmartFilter(product, query) {
  const q = safeStr(query).trim();
  if (!q) return true;

  const clauses = q
    .split(/\s*(?:,|;|\band\b)\s*/i)
    .map((x) => x.trim())
    .filter(Boolean);

  if (!clauses.length) return true;

  return clauses.every((clause) => {
    const opMatch = clause.match(/^(.+?)\s*(>=|<=|>|<|=|:)\s*(.+)$/);

    if (opMatch) {
      const left = opMatch[1].trim();
      const op = opMatch[2].trim();
      const value = opMatch[3].trim();

      const field = smartAliasToField(left);
      if (!field) return true;

      let operator = "contains";

      if (op === ">") operator = "gt";
      if (op === ">=") operator = "gte";
      if (op === "<") operator = "lt";
      if (op === "<=") operator = "lte";
      if (op === "=") operator = "equals";
      if (op === ":") operator = "contains";

      return applyFieldFilterRule(product, {
        field,
        operator,
        value,
      });
    }

    const wordMatch = clause.match(
      /^(category|cat|catageory|sub category|subcategory|subcatageory|company|company name|board|quality|name|product)\s+(.+)$/i,
    );

    if (wordMatch) {
      const field = smartAliasToField(wordMatch[1]);
      const value = wordMatch[2];

      return applyFieldFilterRule(product, {
        field,
        operator: "contains",
        value,
      });
    }

    const words = clause
      .toLowerCase()
      .split(/\s+/)
      .map((x) => x.trim())
      .filter(Boolean);

    const productText = fields
      .map((f) => safeStr(product?.[f]))
      .join(" ")
      .toLowerCase();

    return words.every((w) => productText.includes(w));
  });
}

/* ======================= SUMMARY RIBBON ======================= */
function SummaryRibbon({ data = {} }) {
  const items = [
    { label: "Gsm", value: data?.gsm ?? "0.00" },
    { label: "Amount", value: data?.amount ?? "0.00" },
    { label: "GST Amount", value: data?.gstAmount ?? "0.00" },
    { label: "Total Amount", value: data?.totalAmount ?? "0.00" },
    { label: "Total Cost", value: data?.totalCost ?? "0.00" },
    { label: "Cost / Pcs", value: data?.costPerPcs ?? "0.00" },
    { label: "Profit / Piece", value: data?.profitPerPiece ?? "0.00" },
  ];

  return (
    <Box
      sx={{
        p: 1.1,
        borderRadius: 2,
        background: "linear-gradient(135deg,#ffffff,#f7fafc)",
        border: `1px solid ${UI.border}`,
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
      }}
    >
      {items.map((item) => (
        <Box
          key={item.label}
          sx={{
            minWidth: 130,
            flex: "1 1 130px",
            p: 1,
            borderRadius: 1.8,
            background: "#fff",
            border: `1px solid ${UI.border}`,
          }}
        >
          <Typography sx={{ fontSize: 11.5, color: UI.sub, fontWeight: 900 }}>
            {item.label}
          </Typography>

          <Typography sx={{ fontSize: 15, color: UI.text, fontWeight: 1000 }}>
            {item.value || "0.00"}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

/* ======================= WIDTH CONTROL ======================= */
function WidthControl({ boxWidth, setBoxWidth }) {
  return (
    <Box
      sx={{
        p: 1.2,
        borderRadius: 2,
        border: `1px solid ${UI.border}`,
        background: "#fff",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Typography sx={{ fontWeight: 1000, fontSize: 13.5, color: UI.text }}>
          Field Box Width
        </Typography>

        <Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap" }}>
          {[160, 190, 230].map((v) => (
            <Button
              key={v}
              size="small"
              variant={boxWidth === v ? "contained" : "outlined"}
              onClick={() => setBoxWidth(v)}
              sx={{
                minWidth: 74,
                fontWeight: 900,
                textTransform: "none",
                ...(boxWidth === v
                  ? {
                      background: UI.amazonOrangeDeep,
                      color: "#111",
                      "&:hover": { background: UI.amazonOrange },
                    }
                  : {}),
              }}
            >
              {v === 160 ? "Compact" : v === 190 ? "Normal" : "Wide"}
            </Button>
          ))}
        </Box>
      </Box>

      <Box sx={{ px: 1, mt: 1 }}>
        <Slider
          value={Number(boxWidth) || 190}
          min={140}
          max={280}
          step={10}
          onChange={(_, val) => setBoxWidth(Array.isArray(val) ? val[0] : val)}
          valueLabelDisplay="auto"
        />
      </Box>
    </Box>
  );
}

/* ======================= GROUP SECTION ======================= */
function GroupSection({
  title,
  fields,
  bg,
  border,
  isEditing,
  formData,
  product,
  calculatedFields,
  onChangeField,
  boardQualityOptions,
  orderSheetGsmOptions,
  suggestions,
  enableSuggest,
  dense = false,
  splitManualFirst = false,
  boxWidth = 190,
}) {
  const suggestFields = ["category", "subCategory", "companyName", "name"];

  const fieldMultiplier = (k) => {
    const map = {
      name: 1.8,
      companyName: 1.45,
      category: 1.2,
      subCategory: 1.25,
      boardQuality: 1.25,
      orderSheetGsm: 1.1,
      orderSheetL: 1.05,
      orderSheetB: 1.05,
      salePrice: 1.05,
      totalAmount: 1.15,
      totalCost: 1.15,
      profitPerPiece: 1.15,
      pricePerPiece: 1.1,
    };

    return map[k] || 1;
  };

  const inputSx = {
    mt: dense ? 0.45 : 0.7,
    width: "100%",
    "& .MuiOutlinedInput-root": {
      borderRadius: 1.8,
      background: "#fff",
      height: dense ? 36 : 40,
    },
    "& input": {
      fontWeight: 800,
      fontSize: dense ? 12.5 : 13,
      py: 0.7,
    },
    "& label": {
      fontWeight: 900,
    },
  };

  const wrapRowSx = {
    display: "flex",
    flexWrap: "wrap",
    gap: dense ? 0.8 : 1,
    alignItems: "stretch",
  };

  const cardSx = (realKey) => {
    const width = Math.max(
      140,
      Math.round(boxWidth * fieldMultiplier(realKey)),
    );

    return {
      borderRadius: 2,
      background: "#fff",
      border: `1px solid ${UI.border}`,
      p: dense ? 0.8 : 1,
      width: { xs: "100%", sm: width },
      minWidth: { xs: "100%", sm: width },
      boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    };
  };

  const entries = useMemo(() => {
    return fields.map((field) => {
      const realKey = field;
      const disabled = calculatedFields.includes(realKey);
      const valueToShow = isEditing ? formData?.[realKey] : product?.[realKey];

      return {
        field,
        shownLabel: labelize(field),
        realKey,
        disabled,
        valueToShow,
        isBoardQualityField: realKey === "boardQuality",
        isOrderSheetGsmField: realKey === "orderSheetGsm",
        isSuggestField: !!enableSuggest && suggestFields.includes(realKey),
      };
    });
  }, [fields, calculatedFields, isEditing, formData, product, enableSuggest]);

  const manualEntries = useMemo(
    () => entries.filter((e) => !e.disabled),
    [entries],
  );

  const autoEntries = useMemo(
    () => entries.filter((e) => e.disabled),
    [entries],
  );

  const renderEntry = (e) => {
    const { field, shownLabel, realKey, disabled, valueToShow } = e;

    return (
      <Box key={field} sx={cardSx(realKey)}>
        <Typography
          sx={{
            fontSize: dense ? 11 : 12,
            color: UI.sub,
            fontWeight: 1000,
            lineHeight: 1.12,
          }}
        >
          {shownLabel}
        </Typography>

        {isEditing ? (
          e.isBoardQualityField ? (
            <FormControl size="small" sx={inputSx}>
              <InputLabel>Board Quality</InputLabel>
              <Select
                label="Board Quality"
                value={formData?.boardQuality || ""}
                onChange={(ev) =>
                  onChangeField("boardQuality", ev.target.value)
                }
              >
                {(boardQualityOptions || []).map((x) => (
                  <MenuItem key={x} value={x}>
                    {x}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : e.isOrderSheetGsmField &&
            Array.isArray(orderSheetGsmOptions) &&
            orderSheetGsmOptions.length ? (
            <FormControl size="small" sx={inputSx}>
              <InputLabel>Order Sheet Gsm</InputLabel>
              <Select
                label="Order Sheet Gsm"
                value={formData?.orderSheetGsm ?? ""}
                onChange={(ev) =>
                  onChangeField("orderSheetGsm", ev.target.value)
                }
              >
                {orderSheetGsmOptions.map((x) => (
                  <MenuItem key={x} value={x}>
                    {x}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : e.isSuggestField ? (
            <Autocomplete
              freeSolo
              size="small"
              options={(suggestions?.[realKey] || []).slice(0, 25)}
              inputValue={formData?.[realKey] ?? ""}
              onInputChange={(_, newVal) => onChangeField(realKey, newVal)}
              onChange={(_, newVal) => onChangeField(realKey, newVal ?? "")}
              disabled={disabled}
              renderInput={(params) => (
                <TextField {...params} size="small" sx={inputSx} />
              )}
            />
          ) : (
            <TextField
              size="small"
              value={formData?.[realKey] ?? ""}
              onChange={(ev) => onChangeField(realKey, ev.target.value)}
              disabled={disabled}
              sx={inputSx}
              type={
                [
                  "category",
                  "subCategory",
                  "companyName",
                  "boardQuality",
                  "name",
                ].includes(realKey)
                  ? "text"
                  : "number"
              }
            />
          )
        ) : (
          <Typography
            sx={{
              mt: dense ? 0.45 : 0.6,
              fontSize: dense ? 13 : 13.5,
              color: UI.text,
              fontWeight: 900,
              lineHeight: 1.1,
            }}
          >
            {valueToShow != null && valueToShow !== "" ? valueToShow : "—"}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        borderRadius: 2.3,
        overflow: "hidden",
        border: `1px solid ${border}`,
        background: bg,
        boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
      }}
    >
      <Box
        sx={{
          px: dense ? 1 : 1.3,
          py: dense ? 0.8 : 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "rgba(255,255,255,0.75)",
          borderBottom: `1px solid ${border}`,
        }}
      >
        <Typography
          sx={{ fontWeight: 1100, color: UI.text, fontSize: dense ? 13 : 14 }}
        >
          {title}
        </Typography>

        <Box
          sx={{ width: 10, height: 10, borderRadius: 999, background: border }}
        />
      </Box>

      <Box sx={{ p: dense ? 1 : 1.2 }}>
        {splitManualFirst && isEditing ? (
          <>
            <Box sx={wrapRowSx}>{manualEntries.map(renderEntry)}</Box>

            {autoEntries.length > 0 && (
              <>
                <Typography
                  sx={{
                    mt: 1.2,
                    mb: 0.8,
                    fontWeight: 1000,
                    color: UI.sub,
                    fontSize: 12,
                  }}
                >
                  Auto Calculated
                </Typography>

                <Box sx={wrapRowSx}>{autoEntries.map(renderEntry)}</Box>
              </>
            )}
          </>
        ) : (
          <Box sx={wrapRowSx}>{entries.map(renderEntry)}</Box>
        )}
      </Box>
    </Box>
  );
}

/* ======================= PRODUCT CARD ======================= */
function ProductCard({
  product,
  isOpen,
  isMatch,
  isSelected,
  onToggleSelect,
  onOpen,
  onCreateOrderSingle,
}) {
  const accent = colorFromId(product?._id);

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: isOpen
          ? UI.amazonOrangeDeep
          : isMatch
            ? "#bdbdbd"
            : UI.border,
        borderRadius: 2.2,
        background: UI.cardBg,
        overflow: "hidden",
        boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
        transition: "transform 0.12s ease",
        "&:hover": { transform: "translateY(-2px)" },
        position: "relative",
        height: "100%",
      }}
    >
      <Box sx={{ height: 6, background: accent }} />

      <Box
        sx={{ position: "absolute", top: 8, right: 8, zIndex: 3 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Tooltip title="Create Order (this product)" arrow>
          <IconButton
            size="small"
            onClick={() => onCreateOrderSingle(product)}
            sx={{
              background: UI.amazonOrangeDeep,
              color: "#111",
              border: `1px solid ${UI.amazonOrange}`,
              "&:hover": { background: UI.amazonOrange },
            }}
          >
            <AddShoppingCartIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          position: "absolute",
          bottom: 8,
          right: 8,
          zIndex: 2,
          background: "rgba(255,255,255,0.92)",
          borderRadius: 1.2,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Checkbox
          size="small"
          checked={isSelected}
          onChange={() => onToggleSelect(product._id)}
        />
      </Box>

      <CardActionArea onClick={() => onOpen(product)}>
        <CardContent sx={{ pb: 2 }}>
          <Typography
            sx={{ fontWeight: 900, color: UI.text, fontSize: 16, pr: 4 }}
          >
            {product.name || "Unnamed Product"}
          </Typography>

          <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {product.category ? (
              <Chip
                label={product.category}
                size="small"
                sx={{
                  fontWeight: 900,
                  bgcolor: "#E8F4FF",
                  border: "1px solid #B6D8FF",
                }}
              />
            ) : null}

            {product.subCategory ? (
              <Chip
                label={product.subCategory}
                size="small"
                sx={{
                  fontWeight: 900,
                  bgcolor: "#F0FDF4",
                  border: "1px solid #BBF7D0",
                }}
              />
            ) : null}

            {product.boardQuality ? (
              <Chip
                label={product.boardQuality}
                size="small"
                sx={{
                  fontWeight: 900,
                  bgcolor: "#FFF3E0",
                  border: `1px solid ${UI.amazonOrange}`,
                }}
              />
            ) : null}
          </Box>

          <Box sx={{ mt: 1.2 }}>
            <Typography sx={{ fontSize: 13, color: UI.sub }}>
              Total Amount:{" "}
              <Box
                component="span"
                sx={{ fontWeight: 900, color: UI.linkBlue }}
              >
                {product.totalAmount ?? "—"}
              </Box>
            </Typography>

            <Typography sx={{ fontSize: 13, color: UI.sub }}>
              Total Cost:{" "}
              <Box component="span" sx={{ fontWeight: 900, color: "#B12704" }}>
                {product.totalCost ?? "—"}
              </Box>
            </Typography>

            <Typography sx={{ fontSize: 13, color: UI.sub }}>
              Profit / Piece:{" "}
              <Box component="span" sx={{ fontWeight: 900, color: "#007600" }}>
                {product.profitPerPiece ?? "—"}
              </Box>
            </Typography>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

/* ======================= DETAILS PANEL ======================= */
function DetailsPanel({
  product,
  mode,
  formData,
  onStartEdit,
  onSaveUpdate,
  onCancelEdit,
  onClose,
  onChangeField,
  boardQualityOptions,
  orderSheetGsmOptions,
  boxWidth,
  setBoxWidth,
}) {
  const isEditing = mode === "edit";
  const displayData =
    Object.keys(formData || {}).length > 0 ? formData : product || {};

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: UI.amazonOrangeDeep,
        borderRadius: 2.4,
        overflow: "hidden",
        boxShadow: "0 6px 18px rgba(0,0,0,0.10)",
      }}
    >
      <Box sx={{ height: 6, background: UI.amazonOrangeDeep }} />

      <Box sx={{ p: 2, background: "linear-gradient(180deg,#f9fbfc,#ffffff)" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 1000, color: UI.text, fontSize: 18 }}>
              {displayData?.name || "Details"}
            </Typography>

            <Box sx={{ mt: 0.7, display: "flex", gap: 1, flexWrap: "wrap" }}>
              {displayData?.category ? (
                <Chip
                  label={displayData.category}
                  size="small"
                  sx={{
                    fontWeight: 900,
                    bgcolor: "#E8F4FF",
                    border: "1px solid #B6D8FF",
                  }}
                />
              ) : null}

              {displayData?.subCategory ? (
                <Chip
                  label={displayData.subCategory}
                  size="small"
                  sx={{
                    fontWeight: 900,
                    bgcolor: "#F0FDF4",
                    border: "1px solid #BBF7D0",
                  }}
                />
              ) : null}

              {displayData?.boardQuality ? (
                <Chip
                  label={displayData.boardQuality}
                  size="small"
                  sx={{
                    fontWeight: 900,
                    bgcolor: "#FFF3E0",
                    border: `1px solid ${UI.amazonOrange}`,
                  }}
                />
              ) : null}

              {displayData?.companyName ? (
                <Chip
                  label={displayData.companyName}
                  size="small"
                  sx={{
                    fontWeight: 900,
                    bgcolor: "#F0F2F2",
                    border: `1px solid ${UI.border}`,
                  }}
                />
              ) : null}
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {!isEditing && (
              <Button
                size="small"
                variant="contained"
                onClick={onStartEdit}
                sx={{
                  background: UI.amazonOrangeDeep,
                  color: "#111",
                  fontWeight: 900,
                  "&:hover": { background: UI.amazonOrange },
                }}
              >
                Edit
              </Button>
            )}

            {isEditing && (
              <>
                <Button
                  size="small"
                  variant="contained"
                  onClick={onSaveUpdate}
                  sx={{
                    background: UI.amazonOrangeDeep,
                    color: "#111",
                    fontWeight: 900,
                    "&:hover": { background: UI.amazonOrange },
                  }}
                >
                  Save
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  onClick={onCancelEdit}
                  sx={{ fontWeight: 900 }}
                >
                  Cancel
                </Button>
              </>
            )}

            <Button
              size="small"
              variant="text"
              onClick={onClose}
              sx={{ fontWeight: 900, color: UI.linkBlue }}
            >
              Close
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
          <SummaryRibbon data={displayData} />

          <WidthControl boxWidth={boxWidth} setBoxWidth={setBoxWidth} />

          {GROUPS.map((g) => (
            <GroupSection
              key={g.title}
              title={g.title}
              fields={g.fields}
              bg={g.accentBg}
              border={g.accentBorder}
              isEditing={isEditing}
              formData={formData}
              product={displayData}
              calculatedFields={calculatedFields}
              onChangeField={onChangeField}
              boardQualityOptions={boardQualityOptions}
              orderSheetGsmOptions={orderSheetGsmOptions}
              dense={false}
              splitManualFirst={isEditing}
              boxWidth={boxWidth}
            />
          ))}
        </Box>
      </Box>
    </Card>
  );
}

/* ======================= FILTER DIALOG ======================= */
function AdvancedFilterDialog({
  open,
  onClose,
  filterRules,
  setFilterRules,
  smartFilter,
  setSmartFilter,
  onClearAll,
}) {
  const addRule = () => {
    setFilterRules((prev) => [...prev, { ...DEFAULT_FILTER_RULE }]);
  };

  const updateRule = (index, key, value) => {
    setFilterRules((prev) =>
      prev.map((r, i) => (i === index ? { ...r, [key]: value } : r)),
    );
  };

  const removeRule = (index) => {
    setFilterRules((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          fontWeight: 1100,
          background: "linear-gradient(90deg,#131921,#1f2937)",
          color: "#fff",
        }}
      >
        All Field Filters / Smart AI Filter
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          background: "linear-gradient(180deg,#F7F8FA,#FFFFFF)",
          p: 1.4,
        }}
      >
        <Box
          sx={{
            border: `1px solid ${UI.border}`,
            borderRadius: 3,
            background: "#fff",
            p: 1.2,
            mb: 1.2,
          }}
        >
          <Typography sx={{ fontWeight: 1100, mb: 0.7 }}>
            Smart AI Filter
          </Typography>

          <TextField
            size="small"
            fullWidth
            value={smartFilter}
            onChange={(e) => setSmartFilter(e.target.value)}
            placeholder="Example: category SBS, subcategory printed, name 8x8x5, profit > 0"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                background: "#fff",
              },
              "& input": {
                fontWeight: 800,
                fontSize: 13,
              },
            }}
          />

          <Typography
            sx={{ mt: 0.7, fontSize: 12, color: UI.sub, fontWeight: 800 }}
          >
            Use words like: category, subcategory, company, board, name, gsm,
            sale, qty, amount, gst, total, cost, totalcost, profit.
          </Typography>
        </Box>

        <Box
          sx={{
            border: `1px solid ${UI.border}`,
            borderRadius: 3,
            background: "#fff",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 1.3,
              py: 1,
              background: "#EEF6FF",
              borderBottom: `1px solid ${UI.border}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography sx={{ fontWeight: 1100 }}>Field Filters</Typography>

            <Button
              size="small"
              variant="contained"
              onClick={addRule}
              sx={{
                background: UI.amazonOrangeDeep,
                color: "#111",
                fontWeight: 1000,
                "&:hover": { background: UI.amazonOrange },
              }}
            >
              Add Field Filter
            </Button>
          </Box>

          <Box
            sx={{ p: 1.2, display: "flex", flexDirection: "column", gap: 1 }}
          >
            {filterRules.length === 0 ? (
              <Typography sx={{ color: UI.sub, fontWeight: 800, fontSize: 13 }}>
                No field filter added. Click “Add Field Filter” to filter by any
                field.
              </Typography>
            ) : (
              filterRules.map((rule, index) => {
                const numeric = isNumericFilterField(rule.field);

                const allowedOps = numeric
                  ? OPERATOR_OPTIONS
                  : OPERATOR_OPTIONS.filter(
                      (x) => !["gt", "gte", "lt", "lte"].includes(x.value),
                    );

                return (
                  <Box
                    key={index}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "1.1fr 1fr 1.4fr auto",
                      },
                      gap: 1,
                      alignItems: "center",
                      p: 1,
                      borderRadius: 2,
                      border: `1px solid ${UI.border}`,
                      background: "#FAFAFA",
                    }}
                  >
                    <FormControl size="small" fullWidth>
                      <InputLabel>Field</InputLabel>
                      <Select
                        label="Field"
                        value={rule.field}
                        onChange={(e) =>
                          updateRule(index, "field", e.target.value)
                        }
                      >
                        {fields.map((f) => (
                          <MenuItem key={f} value={f}>
                            {labelize(f)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl size="small" fullWidth>
                      <InputLabel>Condition</InputLabel>
                      <Select
                        label="Condition"
                        value={rule.operator}
                        onChange={(e) =>
                          updateRule(index, "operator", e.target.value)
                        }
                      >
                        {allowedOps.map((op) => (
                          <MenuItem key={op.value} value={op.value}>
                            {op.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      size="small"
                      label="Value"
                      value={rule.value}
                      type={numeric ? "number" : "text"}
                      onChange={(e) =>
                        updateRule(index, "value", e.target.value)
                      }
                      fullWidth
                    />

                    <Button
                      size="small"
                      onClick={() => removeRule(index)}
                      sx={{
                        fontWeight: 1000,
                        color: "#B12704",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Remove
                    </Button>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", px: 1.5 }}>
        <Button
          onClick={onClearAll}
          startIcon={<RestartAltIcon />}
          sx={{ fontWeight: 1000, color: "#B12704" }}
        >
          Clear All Filters
        </Button>

        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            background: UI.amazonOrangeDeep,
            color: "#111",
            fontWeight: 1100,
            "&:hover": { background: UI.amazonOrange },
          }}
        >
          Apply Filters
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ======================= BOARD SETUP ======================= */
const BOARD_LS_KEY = "board_setup_last_values_v1";

function loadBoardLast() {
  try {
    const raw = localStorage.getItem(BOARD_LS_KEY);
    if (!raw) return null;

    const obj = JSON.parse(raw);
    return obj && typeof obj === "object" ? obj : null;
  } catch {
    return null;
  }
}

function saveBoardLast(obj) {
  try {
    localStorage.setItem(BOARD_LS_KEY, JSON.stringify(obj || {}));
  } catch {}
}

function normalizeBoardCfgFromApi(x) {
  const A = Array.isArray(x?.productGsm?.A) ? x.productGsm.A : [];
  const B = Array.isArray(x?.productGsm?.B) ? x.productGsm.B : [];

  const pt = safeStr(x?.printType).trim();
  const printType = norm(pt) === "printed" ? "Printed" : "Non Printed";

  return {
    ...x,
    boardQuality: safeStr(x?.boardQuality).trim(),
    printType,
    isPrinted: x?.isPrinted ?? printType === "Printed",

    landedCost: x?.landedCost ?? "",
    processingCost: x?.processingCost ?? "",
    conversionCost: x?.conversionCost ?? "",

    productGstRate: x?.productGstRate ?? "",
    paperGstRate: x?.paperGstRate ?? "",

    productGsm: { A, B },

    printingPer1000: x?.printingPer1000 ?? "",
    plateCharge: x?.plateCharge ?? "",
    cuttingPer1000: x?.cuttingPer1000 ?? "",
    conversionCostPercent: x?.conversionCostPercent ?? "",
    miscCostPercent: x?.miscCostPercent ?? "",
  };
}

function BoardSetupDialog({ open, onClose, boardCfgs, onSaved }) {
  const [selectedBQ, setSelectedBQ] = useState("");
  const [customBQ, setCustomBQ] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  const baseEmpty = {
    boardQuality: "",
    printType: "Non Printed",

    landedCost: "",
    processingCost: "",
    conversionCost: "",

    printingPer1000: "",
    plateCharge: "",
    cuttingPer1000: "",
    conversionCostPercent: "",
    miscCostPercent: "",

    productGstRate: "",
    paperGstRate: "",

    gsmA: [""],
    gsmB: [""],
  };

  const [form, setForm] = useState(baseEmpty);
  const isNonPrinted = form.printType === "Non Printed";

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      background: "#fff",
      minHeight: 40,
    },
    "& input": {
      fontWeight: 800,
      fontSize: 13,
      padding: "10px 10px",
    },
    "& label": {
      fontWeight: 900,
      fontSize: 13,
    },
  };

  const sectionSx = {
    borderRadius: 3,
    border: `1px solid ${UI.border}`,
    background: "#fff",
    overflow: "hidden",
    boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
  };

  const sectionHeadSx = (bg = "#F7F8FA") => ({
    px: 1.4,
    py: 1,
    background: bg,
    borderBottom: `1px solid ${UI.border}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 1,
    flexWrap: "wrap",
  });

  const fieldCardSx = (bg = "#fff") => ({
    p: 1,
    borderRadius: 2,
    border: `1px solid ${UI.border}`,
    background: bg,
  });

  const gridSx = {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: "repeat(2, minmax(0, 1fr))",
      md: "repeat(4, minmax(0, 1fr))",
    },
    gap: 1,
  };

  const setField = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
  };

  const setPrintType = (val) => {
    const printType = val === "Printed" ? "Printed" : "Non Printed";

    setForm((p) => {
      if (printType === "Non Printed") {
        return {
          ...p,
          printType,
          conversionCost: "",
          printingPer1000: "",
          plateCharge: "",
        };
      }

      return {
        ...p,
        printType,
      };
    });
  };

  const setGsmValue = (group, idx, v) => {
    setForm((p) => {
      const key = group === "A" ? "gsmA" : "gsmB";
      const arr = [...(p[key] || [])];

      arr[idx] = v;

      return { ...p, [key]: arr };
    });
  };

  const addGsmRow = (group) => {
    setForm((p) => {
      const key = group === "A" ? "gsmA" : "gsmB";
      return { ...p, [key]: [...(p[key] || []), ""] };
    });
  };

  const removeGsmRow = (group, idx) => {
    setForm((p) => {
      const key = group === "A" ? "gsmA" : "gsmB";
      const arr = [...(p[key] || [])];

      if (arr.length <= 1) return p;

      arr.splice(idx, 1);

      return { ...p, [key]: arr };
    });
  };

  const bqOptions = useMemo(() => {
    const arr = (boardCfgs || [])
      .map((x) => safeStr(x?.boardQuality).trim())
      .filter(Boolean);

    if (selectedBQ && !arr.includes(selectedBQ)) arr.unshift(selectedBQ);

    return Array.from(new Set(arr)).sort((a, b) => a.localeCompare(b));
  }, [boardCfgs, selectedBQ]);

  useEffect(() => {
    if (!open) return;

    const last = loadBoardLast();

    setSelectedBQ("");
    setCustomBQ("");
    setEditingId(null);
    setLoading(false);

    if (last) {
      setForm({
        ...baseEmpty,
        ...last,
        boardQuality: "",
        gsmA: Array.isArray(last.gsmA) && last.gsmA.length ? last.gsmA : [""],
        gsmB: Array.isArray(last.gsmB) && last.gsmB.length ? last.gsmB : [""],
        printType: last.printType === "Printed" ? "Printed" : "Non Printed",
      });
    } else {
      setForm(baseEmpty);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pickExisting = (bq) => {
    const boardName = safeStr(bq).trim();

    const hit =
      (boardCfgs || []).find(
        (x) => norm(x?.boardQuality) === norm(boardName),
      ) || null;

    if (!hit) {
      setEditingId(null);
      setForm((p) => ({
        ...p,
        boardQuality: boardName,
      }));
      return;
    }

    setEditingId(hit?._id || null);

    setForm({
      boardQuality: safeStr(hit?.boardQuality),
      printType: norm(hit?.printType) === "printed" ? "Printed" : "Non Printed",

      landedCost: safeStr(hit?.landedCost ?? ""),
      processingCost: safeStr(hit?.processingCost ?? ""),
      conversionCost: safeStr(hit?.conversionCost ?? ""),

      printingPer1000: safeStr(hit?.printingPer1000 ?? ""),
      plateCharge: safeStr(hit?.plateCharge ?? ""),
      cuttingPer1000: safeStr(hit?.cuttingPer1000 ?? ""),
      conversionCostPercent: safeStr(hit?.conversionCostPercent ?? ""),
      miscCostPercent: safeStr(hit?.miscCostPercent ?? ""),

      productGstRate: safeStr(hit?.productGstRate ?? ""),
      paperGstRate: safeStr(hit?.paperGstRate ?? ""),

      gsmA:
        Array.isArray(hit?.productGsm?.A) && hit.productGsm.A.length
          ? hit.productGsm.A.map((n) => safeStr(n))
          : [""],

      gsmB:
        Array.isArray(hit?.productGsm?.B) && hit.productGsm.B.length
          ? hit.productGsm.B.map((n) => safeStr(n))
          : [""],
    });
  };

  const onSelectBoardQuality = (val) => {
    setSelectedBQ(val);
    setCustomBQ("");
    pickExisting(val);
  };

  const applyCustomBoardQuality = () => {
    const bq = safeStr(customBQ).trim();
    if (!bq) return;

    setSelectedBQ(bq);
    pickExisting(bq);
  };

  const buildPayload = () => {
    const A = (form.gsmA || [])
      .map((x) => toNum(x))
      .filter((n) => Number.isFinite(n));

    const B = (form.gsmB || [])
      .map((x) => toNum(x))
      .filter((n) => Number.isFinite(n));

    const boardQuality =
      safeStr(form.boardQuality).trim() || safeStr(selectedBQ).trim();

    const printType = form.printType === "Printed" ? "Printed" : "Non Printed";

    return {
      boardQuality,
      printType,
      isPrinted: printType === "Printed",

      landedCost: toNum(form.landedCost) ?? 0,
      processingCost: toNum(form.processingCost) ?? 0,

      conversionCost:
        printType === "Non Printed" ? 0 : (toNum(form.conversionCost) ?? 0),

      printingPer1000:
        printType === "Non Printed" ? 0 : (toNum(form.printingPer1000) ?? 0),

      plateCharge:
        printType === "Non Printed" ? 0 : (toNum(form.plateCharge) ?? 0),

      cuttingPer1000: toNum(form.cuttingPer1000) ?? 0,
      conversionCostPercent: toNum(form.conversionCostPercent) ?? 0,
      miscCostPercent: toNum(form.miscCostPercent) ?? 0,

      productGstRate: toNum(form.productGstRate) ?? 0,
      paperGstRate: toNum(form.paperGstRate) ?? 0,

      productGsm: { A, B },
    };
  };

  const save = async () => {
    const payload = buildPayload();

    if (!payload.boardQuality) {
      alert("Please select or create a Board Quality first.");
      return;
    }

    setLoading(true);

    try {
      await axios.post(BOARD_SETUP_URL, payload);

      saveBoardLast({
        printType: payload.printType,

        landedCost: String(payload.landedCost ?? ""),
        processingCost: String(payload.processingCost ?? ""),
        conversionCost: String(payload.conversionCost ?? ""),

        printingPer1000: String(payload.printingPer1000 ?? ""),
        plateCharge: String(payload.plateCharge ?? ""),
        cuttingPer1000: String(payload.cuttingPer1000 ?? ""),
        conversionCostPercent: String(payload.conversionCostPercent ?? ""),
        miscCostPercent: String(payload.miscCostPercent ?? ""),

        productGstRate: String(payload.productGstRate ?? ""),
        paperGstRate: String(payload.paperGstRate ?? ""),

        gsmA: (payload.productGsm?.A || []).map((n) => String(n)),
        gsmB: (payload.productGsm?.B || []).map((n) => String(n)),
      });

      await onSaved?.();
      onClose?.();
    } catch (e) {
      alert(
        e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          "Failed to save board setup",
      );
    } finally {
      setLoading(false);
    }
  };

  const del = async () => {
    if (!editingId) return;

    const ok = window.confirm(
      "Delete this Board Quality? This cannot be undone.",
    );
    if (!ok) return;

    setLoading(true);

    try {
      await axios.delete(`${BOARD_SETUP_URL}/${editingId}`);
      await onSaved?.();

      setSelectedBQ("");
      setCustomBQ("");
      setEditingId(null);
      setForm(baseEmpty);

      onClose?.();
    } catch (e) {
      alert(
        e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          "Failed to delete board setup",
      );
    } finally {
      setLoading(false);
    }
  };

  const hasBoardName =
    !!safeStr(form.boardQuality).trim() || !!safeStr(selectedBQ).trim();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          py: 1.2,
          px: 1.6,
          background: "linear-gradient(90deg,#131921,#1f2937)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 1100, fontSize: 18 }}>
            Board Setup
          </Typography>

          <Typography sx={{ color: "rgba(255,255,255,0.72)", fontSize: 12 }}>
            Create board, select print type, set costing, GST and GSM in one
            page
          </Typography>
        </Box>

        <Chip
          label={editingId ? "Editing Existing Board" : "New / Unsaved Board"}
          size="small"
          sx={{
            fontWeight: 1000,
            background: editingId ? "#DCFCE7" : "#FEF3C7",
            color: editingId ? "#166534" : "#92400E",
          }}
        />
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: 1.3,
          background: "linear-gradient(180deg,#F7F8FA,#FFFFFF)",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
          <Box sx={sectionSx}>
            <Box sx={sectionHeadSx("#EEF2FF")}>
              <Typography sx={{ fontWeight: 1100, color: UI.text }}>
                Board Information
              </Typography>

              {hasBoardName ? (
                <Typography
                  sx={{ fontWeight: 900, color: UI.linkBlue, fontSize: 13 }}
                >
                  Current: {form.boardQuality || selectedBQ}
                </Typography>
              ) : (
                <Typography
                  sx={{ fontWeight: 900, color: "#B12704", fontSize: 13 }}
                >
                  Please select or create board
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                p: 1.2,
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "1.2fr 1.5fr 1fr",
                },
                gap: 1,
                alignItems: "stretch",
              }}
            >
              <Box sx={fieldCardSx("#F8FAFC")}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Existing Board Quality</InputLabel>
                  <Select
                    label="Existing Board Quality"
                    value={selectedBQ}
                    onChange={(e) => onSelectBoardQuality(e.target.value)}
                  >
                    {bqOptions.map((x) => (
                      <MenuItem key={x} value={x}>
                        {x}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={fieldCardSx("#FFF7ED")}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <TextField
                    size="small"
                    label="Create Board Quality"
                    placeholder="Type new board quality..."
                    value={customBQ}
                    onChange={(e) => setCustomBQ(e.target.value)}
                    fullWidth
                    sx={inputSx}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") applyCustomBoardQuality();
                    }}
                  />

                  <Button
                    variant="contained"
                    onClick={applyCustomBoardQuality}
                    sx={{
                      height: 40,
                      px: 2,
                      background: UI.amazonOrangeDeep,
                      color: "#111",
                      fontWeight: 1000,
                      whiteSpace: "nowrap",
                      "&:hover": { background: UI.amazonOrange },
                    }}
                  >
                    Use
                  </Button>
                </Box>
              </Box>

              <Box sx={fieldCardSx("#F0FDF4")}>
                <Typography
                  sx={{
                    fontWeight: 1000,
                    fontSize: 12,
                    color: UI.sub,
                    mb: 0.3,
                  }}
                >
                  Print Type
                </Typography>

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.printType === "Printed"}
                        onChange={() => setPrintType("Printed")}
                      />
                    }
                    label="Printed"
                    sx={{
                      mr: 0,
                      "& .MuiFormControlLabel-label": {
                        fontWeight: 900,
                        fontSize: 13,
                      },
                    }}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.printType === "Non Printed"}
                        onChange={() => setPrintType("Non Printed")}
                      />
                    }
                    label="Non Printed"
                    sx={{
                      mr: 0,
                      "& .MuiFormControlLabel-label": {
                        fontWeight: 900,
                        fontSize: 13,
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          <Box sx={sectionSx}>
            <Box sx={sectionHeadSx("#F0FDF4")}>
              <Typography sx={{ fontWeight: 1100, color: UI.text }}>
                Costing, Charges & GST
              </Typography>

              {isNonPrinted ? (
                <Chip
                  label="Printing fields disabled for Non Printed"
                  size="small"
                  sx={{
                    fontWeight: 900,
                    bgcolor: "#FEE2E2",
                    color: "#991B1B",
                  }}
                />
              ) : (
                <Chip
                  label="Printed costing enabled"
                  size="small"
                  sx={{
                    fontWeight: 900,
                    bgcolor: "#DCFCE7",
                    color: "#166534",
                  }}
                />
              )}
            </Box>

            <Box sx={{ p: 1.2 }}>
              <Box sx={gridSx}>
                <Box sx={fieldCardSx("#ECFDF5")}>
                  <TextField
                    type="number"
                    size="small"
                    label="Landed Cost (₹/kg)"
                    value={form.landedCost}
                    onChange={(e) => setField("landedCost", e.target.value)}
                    fullWidth
                    sx={inputSx}
                  />
                </Box>

                <Box sx={fieldCardSx("#ECFDF5")}>
                  <TextField
                    type="number"
                    size="small"
                    label="Profit in KG"
                    value={form.processingCost}
                    onChange={(e) => setField("processingCost", e.target.value)}
                    fullWidth
                    sx={inputSx}
                  />
                </Box>

                <Box sx={fieldCardSx(isNonPrinted ? "#F3F4F6" : "#FFF7ED")}>
                  <TextField
                    type="number"
                    size="small"
                    label="Fixed Printing Charges"
                    value={isNonPrinted ? "" : form.conversionCost}
                    onChange={(e) => setField("conversionCost", e.target.value)}
                    fullWidth
                    sx={inputSx}
                    disabled={isNonPrinted}
                  />
                </Box>

                <Box sx={fieldCardSx(isNonPrinted ? "#F3F4F6" : "#FFF7ED")}>
                  <TextField
                    type="number"
                    size="small"
                    label="Printing Per / 1000"
                    value={isNonPrinted ? "" : form.printingPer1000}
                    onChange={(e) =>
                      setField("printingPer1000", e.target.value)
                    }
                    fullWidth
                    sx={inputSx}
                    disabled={isNonPrinted}
                  />
                </Box>

                <Box sx={fieldCardSx(isNonPrinted ? "#F3F4F6" : "#FFF7ED")}>
                  <TextField
                    type="number"
                    size="small"
                    label="Plate Charge"
                    value={isNonPrinted ? "" : form.plateCharge}
                    onChange={(e) => setField("plateCharge", e.target.value)}
                    fullWidth
                    sx={inputSx}
                    disabled={isNonPrinted}
                  />
                </Box>

                <Box sx={fieldCardSx("#FFF7ED")}>
                  <TextField
                    type="number"
                    size="small"
                    label="Cutting / 1000"
                    value={form.cuttingPer1000}
                    onChange={(e) => setField("cuttingPer1000", e.target.value)}
                    fullWidth
                    sx={inputSx}
                  />
                </Box>

                <Box sx={fieldCardSx("#F5F3FF")}>
                  <TextField
                    type="number"
                    size="small"
                    label="Conversion Cost (%)"
                    value={form.conversionCostPercent}
                    onChange={(e) =>
                      setField("conversionCostPercent", e.target.value)
                    }
                    fullWidth
                    sx={inputSx}
                  />
                </Box>

                <Box sx={fieldCardSx("#F5F3FF")}>
                  <TextField
                    type="number"
                    size="small"
                    label="Misc Cost (%)"
                    value={form.miscCostPercent}
                    onChange={(e) =>
                      setField("miscCostPercent", e.target.value)
                    }
                    fullWidth
                    sx={inputSx}
                  />
                </Box>

                <Box sx={fieldCardSx("#EFF6FF")}>
                  <TextField
                    type="number"
                    size="small"
                    label="Product GST Rate (%)"
                    value={form.productGstRate}
                    onChange={(e) => setField("productGstRate", e.target.value)}
                    fullWidth
                    sx={inputSx}
                  />
                </Box>

                <Box sx={fieldCardSx("#EFF6FF")}>
                  <TextField
                    type="number"
                    size="small"
                    label="Paper GST Rate (%)"
                    value={form.paperGstRate}
                    onChange={(e) => setField("paperGstRate", e.target.value)}
                    fullWidth
                    sx={inputSx}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          <Box sx={sectionSx}>
            <Box sx={sectionHeadSx("#FFF7ED")}>
              <Typography sx={{ fontWeight: 1100, color: UI.text }}>
                Product GSM Groups
              </Typography>

              <Typography sx={{ color: UI.sub, fontSize: 12, fontWeight: 800 }}>
                Order Sheet GSM dropdown will use Group A + Group B
              </Typography>
            </Box>

            <Box
              sx={{
                p: 1.2,
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "1fr 1fr",
                },
                gap: 1.2,
              }}
            >
              <Box
                sx={{
                  border: `1px solid #B8D8FF`,
                  borderRadius: 3,
                  background: "linear-gradient(180deg,#EEF6FF,#FFFFFF)",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    px: 1.2,
                    py: 0.9,
                    borderBottom: `1px solid #B8D8FF`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Typography sx={{ fontWeight: 1100 }}>Group A</Typography>

                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => addGsmRow("A")}
                    sx={{ fontWeight: 900 }}
                  >
                    Add GSM
                  </Button>
                </Box>

                <Box
                  sx={{
                    p: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {(form.gsmA || []).map((val, idx) => (
                    <Box
                      key={`A-${idx}`}
                      sx={{ display: "flex", gap: 1, alignItems: "center" }}
                    >
                      <TextField
                        type="number"
                        size="small"
                        label={`A${idx + 1} GSM`}
                        value={val}
                        onChange={(e) => setGsmValue("A", idx, e.target.value)}
                        fullWidth
                        sx={inputSx}
                      />

                      <Button
                        size="small"
                        variant="text"
                        onClick={() => removeGsmRow("A", idx)}
                        sx={{
                          fontWeight: 900,
                          color: "#B12704",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Remove
                      </Button>
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box
                sx={{
                  border: `1px solid #FFD5A6`,
                  borderRadius: 3,
                  background: "linear-gradient(180deg,#FFF6EC,#FFFFFF)",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    px: 1.2,
                    py: 0.9,
                    borderBottom: `1px solid #FFD5A6`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Typography sx={{ fontWeight: 1100 }}>Group B</Typography>

                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => addGsmRow("B")}
                    sx={{ fontWeight: 900 }}
                  >
                    Add GSM
                  </Button>
                </Box>

                <Box
                  sx={{
                    p: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {(form.gsmB || []).map((val, idx) => (
                    <Box
                      key={`B-${idx}`}
                      sx={{ display: "flex", gap: 1, alignItems: "center" }}
                    >
                      <TextField
                        type="number"
                        size="small"
                        label={`B${idx + 1} GSM`}
                        value={val}
                        onChange={(e) => setGsmValue("B", idx, e.target.value)}
                        fullWidth
                        sx={inputSx}
                      />

                      <Button
                        size="small"
                        variant="text"
                        onClick={() => removeGsmRow("B", idx)}
                        sx={{
                          fontWeight: 900,
                          color: "#B12704",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Remove
                      </Button>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 1.5,
          py: 1.2,
          justifyContent: "space-between",
          background: "#fff",
        }}
      >
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            onClick={onClose}
            startIcon={<CloseIcon />}
            sx={{ fontWeight: 900 }}
          >
            Close
          </Button>

          <Button
            onClick={del}
            disabled={!editingId || loading}
            sx={{
              fontWeight: 1000,
              color: "#B12704",
            }}
          >
            Delete
          </Button>
        </Box>

        <Button
          onClick={save}
          variant="contained"
          disabled={!hasBoardName || loading}
          sx={{
            background: UI.amazonOrangeDeep,
            color: "#111",
            fontWeight: 1100,
            px: 3,
            "&:hover": { background: UI.amazonOrange },
          }}
        >
          {loading
            ? "Saving..."
            : editingId
              ? "Update Board Setup"
              : "Save Board Setup"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ======================= CREATE PRODUCT DIALOG ======================= */
function CreateProductDialog({
  open,
  onClose,
  formData,
  onChangeField,
  onSave,
  saving,
  boardQualityOptions,
  orderSheetGsmOptions,
  suggestions,
  enableSuggest,
  boxWidth,
  setBoxWidth,
}) {
  const steps = useMemo(
    () => [
      { key: "Product Details", label: "1. Product" },
      { key: "Order Details For Production", label: "2. Order" },
      { key: "Costing With Print", label: "3. Costing" },
    ],
    [],
  );

  const [activeKey, setActiveKey] = useState(steps[0].key);

  useEffect(() => {
    if (open) setActiveKey(steps[0].key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const activeGroup = GROUPS.find((g) => g.title === activeKey) || GROUPS[0];

  const idx = steps.findIndex((s) => s.key === activeKey);
  const canBack = idx > 0;
  const canNext = idx < steps.length - 1;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          borderRadius: 3,
          m: { xs: 1, sm: 1.5 },
          width: "100%",
          maxWidth: 1450,
        },
      }}
    >
      <DialogTitle
        sx={{
          py: 1.1,
          px: 1.4,
          display: "flex",
          alignItems: "center",
          gap: 1,
          background: "linear-gradient(90deg,#131921,#1d2a38)",
          color: "#fff",
        }}
      >
        <Typography sx={{ fontWeight: 1100, fontSize: 16 }}>
          Add Product
        </Typography>

        <Box sx={{ flex: 1 }} />

        <Box
          sx={{
            display: "flex",
            gap: 0.7,
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          {steps.map((s) => {
            const active = s.key === activeKey;

            return (
              <Button
                key={s.key}
                size="small"
                variant={active ? "contained" : "outlined"}
                onClick={() => setActiveKey(s.key)}
                sx={{
                  minWidth: "unset",
                  px: 1.1,
                  py: 0.45,
                  borderRadius: 2,
                  fontWeight: 1000,
                  textTransform: "none",
                  borderColor: "rgba(255,255,255,0.35)",
                  ...(active
                    ? {
                        background: UI.amazonOrangeDeep,
                        color: "#111",
                        "&:hover": { background: UI.amazonOrange },
                      }
                    : { color: "#fff" }),
                }}
              >
                {s.label}
              </Button>
            );
          })}
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          background: "linear-gradient(180deg,#f7f8fb,#ffffff)",
          p: 1.2,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
          <SummaryRibbon data={formData} />

          <WidthControl boxWidth={boxWidth} setBoxWidth={setBoxWidth} />

          <GroupSection
            key={activeGroup.title}
            title={activeGroup.title}
            fields={activeGroup.fields}
            bg={activeGroup.accentBg}
            border={activeGroup.accentBorder}
            isEditing={true}
            formData={formData}
            product={formData}
            calculatedFields={calculatedFields}
            onChangeField={onChangeField}
            boardQualityOptions={boardQualityOptions}
            orderSheetGsmOptions={orderSheetGsmOptions}
            suggestions={suggestions}
            enableSuggest={enableSuggest}
            dense={true}
            splitManualFirst={true}
            boxWidth={boxWidth}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 1.2,
          py: 1,
          justifyContent: "space-between",
          borderTop: `1px solid ${UI.border}`,
          background: "#fff",
        }}
      >
        <Button
          onClick={onClose}
          startIcon={<CloseIcon />}
          sx={{ fontWeight: 900 }}
        >
          Cancel
        </Button>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Button
            size="small"
            variant="outlined"
            disabled={!canBack}
            onClick={() => canBack && setActiveKey(steps[idx - 1].key)}
            sx={{ fontWeight: 900 }}
          >
            Back
          </Button>

          <Button
            size="small"
            variant="outlined"
            disabled={!canNext}
            onClick={() => canNext && setActiveKey(steps[idx + 1].key)}
            sx={{ fontWeight: 900 }}
          >
            Next
          </Button>

          <Button
            onClick={onSave}
            variant="contained"
            disabled={saving}
            startIcon={<AddIcon />}
            sx={{
              background: UI.amazonOrangeDeep,
              color: "#111",
              fontWeight: 1100,
              borderRadius: 2,
              "&:hover": { background: UI.amazonOrange },
            }}
          >
            {saving ? "Saving..." : "Create"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

/* ======================= MAIN PAGE ======================= */
const FORCE_STRING_FIELDS = new Set([
  "name",
  "category",
  "subCategory",
  "companyName",
  "boardQuality",
]);

export default function Home() {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [quickCategory, setQuickCategory] = useState("");
  const [quickSubCategory, setQuickSubCategory] = useState("");
  const [quickName, setQuickName] = useState("");

  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filterRules, setFilterRules] = useState([]);
  const [smartFilter, setSmartFilter] = useState("");

  const [expandedId, setExpandedId] = useState(null);

  const [mode, setMode] = useState("view");
  const [formData, setFormData] = useState({});
  const [originalData, setOriginalData] = useState({});

  const fileInputRef = useRef(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkReportOpen, setBulkReportOpen] = useState(false);
  const [bulkReport, setBulkReport] = useState({
    created: [],
    updated: [],
    failed: [],
  });

  const [boardCfgs, setBoardCfgs] = useState([]);
  const [boardDialogOpen, setBoardDialogOpen] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({});
  const [createSaving, setCreateSaving] = useState(false);

  const [editorBoxWidth, setEditorBoxWidth] = useState(() =>
    loadLS(LS_EDITOR_WIDTH_KEY, 190),
  );

  const [suggestions, setSuggestions] = useState(() =>
    loadLS(LS_SUGGEST_KEY, {
      category: [],
      subCategory: [],
      companyName: [],
      name: [],
    }),
  );

  const activeFilterCount = useMemo(() => {
    const quickCount =
      (quickCategory ? 1 : 0) +
      (quickSubCategory ? 1 : 0) +
      (quickName ? 1 : 0);

    const fieldCount = (filterRules || []).filter(
      (r) => r?.field && safeStr(r?.value).trim(),
    ).length;

    const smartCount = safeStr(smartFilter).trim() ? 1 : 0;

    return quickCount + fieldCount + smartCount;
  }, [quickCategory, quickSubCategory, quickName, filterRules, smartFilter]);

  const clearAllFilters = () => {
    setSearchTerm("");
    setQuickCategory("");
    setQuickSubCategory("");
    setQuickName("");
    setFilterRules([]);
    setSmartFilter("");
  };

  useEffect(() => {
    fetchData();
    fetchBoardCfgs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    saveLS(LS_EDITOR_WIDTH_KEY, editorBoxWidth);
  }, [editorBoxWidth]);

  const fetchData = async () => {
    const res = await axios.get(API_URL);
    const arr = Array.isArray(res.data) ? res.data : [];

    setProducts(arr.map(normalizeProductFromApi));
  };

  const fetchBoardCfgs = async () => {
    try {
      const res = await axios.get(BOARD_SETUP_URL);
      const arr = Array.isArray(res.data) ? res.data : [];

      setBoardCfgs(arr.map(normalizeBoardCfgFromApi));
    } catch {
      setBoardCfgs([]);
    }
  };

  const boardQualityOptions = useMemo(() => {
    const set = new Set();

    (boardCfgs || []).forEach((b) => {
      const bq = safeStr(b?.boardQuality).trim();
      if (bq) set.add(bq);
    });

    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [boardCfgs]);

  const saveLastProductToLS = (obj) => saveLS(LS_LAST_PRODUCT_KEY, obj || {});

  const addSuggestionsFromProduct = (p) => {
    setSuggestions((prev) => {
      const next = {
        category: pushUniqueStr(prev?.category, p?.category),
        subCategory: pushUniqueStr(prev?.subCategory, p?.subCategory),
        companyName: pushUniqueStr(prev?.companyName, p?.companyName),
        name: pushUniqueStr(prev?.name, p?.name),
      };

      saveLS(LS_SUGGEST_KEY, next);

      return next;
    });
  };

  /* ------------------ BOARD HELPERS ------------------ */
  const getBoardCfgFor = (boardQuality) => {
    const bq = safeStr(boardQuality).trim().toLowerCase();

    return (
      (boardCfgs || []).find(
        (x) => safeStr(x?.boardQuality).trim().toLowerCase() === bq,
      ) || null
    );
  };

  const isNonPrintedByBoardQuality = (boardQuality) => {
    const cfg = getBoardCfgFor(boardQuality);
    if (!cfg) return false;

    const pt = norm(cfg?.printType);

    if (pt === "non printed" || pt === "nonprinted") return true;
    if (cfg?.isPrinted === false) return true;

    return false;
  };

  const getOrderSheetGsmOptionsForCfg = (cfg) => {
    const A = Array.isArray(cfg?.productGsm?.A) ? cfg.productGsm.A : [];
    const B = Array.isArray(cfg?.productGsm?.B) ? cfg.productGsm.B : [];

    const list = [...A, ...B]
      .map((n) => parseFloat(n))
      .filter((n) => Number.isFinite(n) && n > 0);

    return Array.from(new Set(list)).sort((a, b) => a - b);
  };

  const getOrderSheetGsmOptions = (boardQuality) => {
    const cfg = getBoardCfgFor(boardQuality);
    return getOrderSheetGsmOptionsForCfg(cfg).map((n) => String(n));
  };

  const pickNearestGsm = (productGsmValue, cfg, existingOrderSheetGsm) => {
    const existing = parseFloat(existingOrderSheetGsm);

    if (Number.isFinite(existing) && existing > 0) return existing;

    const v = parseFloat(productGsmValue);
    if (!Number.isFinite(v) || v <= 0) return "";

    const list = getOrderSheetGsmOptionsForCfg(cfg);

    if (!list.length) return Math.ceil(v / 10) * 10;

    let best = list[0];
    let bestDiff = Math.abs(best - v);

    for (const g of list) {
      const d = Math.abs(g - v);

      if (d < bestDiff || (d === bestDiff && g > best)) {
        best = g;
        bestDiff = d;
      }
    }

    return best;
  };

  const getAutoRate = (boardQuality, cfg, existingRate) => {
    const bq = safeStr(boardQuality).trim().toLowerCase();

    if (bq === "itc") return 74;

    const landed = parseFloat(cfg?.landedCost);
    if (Number.isFinite(landed) && landed > 0) return landed;

    const r = parseFloat(existingRate);
    return Number.isFinite(r) ? r : 0;
  };

  /* ------------------ MAIN CALCULATION ------------------ */
  const calculateFields = (product) => {
    const parsed = (val) => parseFloat(val) || 0;

    const normalized = normalizeProductFromApi(product);
    const cfg = getBoardCfgFor(normalized.boardQuality);

    const quantity = parsed(normalized.quantity);

    const lcm = parsed(normalized.lcm);
    const bcm = parsed(normalized.bcm);
    const pcsB = parsed(normalized.pcsBreadth);
    const pcsL = parsed(normalized.pcsLength);
    const wtPer100 = parsed(normalized.wtPer100);
    const bagQnty = parsed(normalized.bagQnty);
    const salePriceNum = parsed(normalized.salePrice);

    const orderSheetB = parsed(normalized.orderSheetB);
    const orderSheetL = parsed(normalized.orderSheetL);

    const wtPer1000 = wtPer100 * 10;

    const gsmNum = lcm && bcm ? wtPer1000 / ((lcm * bcm) / 10000) : 0;

    const pickedOrderSheetGsmNum = pickNearestGsm(
      gsmNum,
      cfg,
      normalized.orderSheetGsm,
    );

    const orderSheetGsmNum = parsed(pickedOrderSheetGsmNum);

    const autoRateNum = getAutoRate(
      normalized.boardQuality,
      cfg,
      normalized.rate,
    );

    const piecePerSheet = pcsB * pcsL;
    const totalPcs = piecePerSheet * quantity;

    const bagWeight = (wtPer100 / 100) * bagQnty;

    const totalWeight =
      ((orderSheetL * orderSheetB * orderSheetGsmNum) / 10000000) * quantity;

    const amount = totalWeight * autoRateNum;

    const cfgPrintingRate = parseFloat(cfg?.printingPer1000);
    const cfgPlate = parseFloat(cfg?.plateCharge);
    const cfgCuttingRate = parseFloat(cfg?.cuttingPer1000);
    const cfgConvPct = parseFloat(cfg?.conversionCostPercent);
    const cfgMiscPct = parseFloat(cfg?.miscCostPercent);
    const cfgPaperGstPct = parseFloat(cfg?.paperGstRate);

    const printingRate = Number.isFinite(cfgPrintingRate) ? cfgPrintingRate : 0;
    const plateChargeRaw = Number.isFinite(cfgPlate) ? cfgPlate : 0;
    const cuttingRate = Number.isFinite(cfgCuttingRate) ? cfgCuttingRate : 0;
    const convPct = Number.isFinite(cfgConvPct) ? cfgConvPct : 0;
    const miscPct = Number.isFinite(cfgMiscPct) ? cfgMiscPct : 0;
    const paperGstPct = Number.isFinite(cfgPaperGstPct) ? cfgPaperGstPct : 0;

    const nonPrinted = isNonPrintedByBoardQuality(normalized.boardQuality);

    const printingCharges = nonPrinted ? 0 : (quantity / 1000) * printingRate;
    const finalPlateCharge = nonPrinted ? 0 : plateChargeRaw;
    const cuttingCharges = (quantity / 1000) * cuttingRate;

    const conversionCharges = (amount * convPct) / 100;
    const miscCharges = (amount * miscPct) / 100;

    const gstAmount = (amount * paperGstPct) / 100;
    const totalAmount = amount + gstAmount;

    const pricePerPiece = totalAmount / (totalPcs || 1);

    const totalCost =
      totalAmount +
      printingCharges +
      finalPlateCharge +
      cuttingCharges +
      conversionCharges +
      miscCharges;

    const costPerPcs = totalCost / (totalPcs || 1);
    const profitPerPiece = salePriceNum - costPerPcs;

    return {
      ...normalized,

      gsm: fmt(gsmNum, 2),
      bagWeight: fmt(bagWeight, 2),

      piecePerSheet: Number.isFinite(piecePerSheet)
        ? piecePerSheet.toFixed(2)
        : "0.00",

      totalPcs: Number.isFinite(totalPcs) ? totalPcs.toFixed(2) : "0.00",

      totalWeight: Number.isFinite(totalWeight)
        ? totalWeight.toFixed(3)
        : "0.000",

      orderSheetGsm:
        pickedOrderSheetGsmNum === "" ? "" : String(pickedOrderSheetGsmNum),

      rate: String(autoRateNum),

      amount: fmt(amount, 2),
      gstAmount: fmt(gstAmount, 2),
      totalAmount: fmt(totalAmount, 2),
      pricePerPiece: fmt(pricePerPiece, 2),

      printingCharges: fmt(printingCharges, 2),
      plateCharge: Number.isFinite(finalPlateCharge)
        ? String(finalPlateCharge)
        : "0",

      cuttingCharges: fmt(cuttingCharges, 2),
      conversionCost: fmt(conversionCharges, 2),
      miscCost: fmt(miscCharges, 2),
      totalCost: fmt(totalCost, 2),
      costPerPcs: fmt(costPerPcs, 2),
      profitPerPiece: fmt(profitPerPiece, 2),

      gstRate: fmt(gstAmount, 2),
      printingPer1000: fmt(printingCharges, 2),
      cuttingPer1000: fmt(cuttingCharges, 2),
      wtPer1000: fmt(wtPer1000, 2),
    };
  };

  /* ✅ Opens product directly in edit mode when clicked from Order Sheet */
  useEffect(() => {
    let openId = "";

    try {
      openId = sessionStorage.getItem(OPEN_PRODUCT_EDIT_KEY) || "";
    } catch {}

    if (!openId || !products.length) return;

    const product = products.find((p) => String(p?._id) === String(openId));
    if (!product) return;

    const final = calculateFields(product);

    clearAllFilters();

    setExpandedId(product._id);
    setMode("edit");
    setFormData(final);
    setOriginalData(final);

    try {
      sessionStorage.removeItem(OPEN_PRODUCT_EDIT_KEY);
    } catch {}

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, boardCfgs]);

  const applyFieldChange = (prev, field, value) => {
    const next = { ...prev, [field]: value };

    if (field === "boardQuality") {
      next.orderSheetGsm = "";
    }

    return calculateFields(next);
  };

  const displayProducts = useMemo(() => {
    return (products || []).map((p) => calculateFields(p));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, boardCfgs]);

  const quickCategoryOptions = useMemo(() => {
    const set = new Set();

    (displayProducts || []).forEach((p) => {
      const v = safeStr(p?.category || p?.catageory).trim();
      if (v) set.add(v);
    });

    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [displayProducts]);

  const quickSubCategoryOptions = useMemo(() => {
    const set = new Set();

    (displayProducts || []).forEach((p) => {
      const v = safeStr(
        p?.subCategory ||
          p?.subcategory ||
          p?.subCatageory ||
          p?.subcatageory ||
          p?.sub_catageory,
      ).trim();

      if (v) set.add(v);
    });

    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [displayProducts]);

  const matchesSearch = (product) => {
    if (!searchTerm) return false;

    const term = norm(searchTerm);

    return fields.some((field) => {
      const val = product[field];
      return val != null && norm(val).includes(term);
    });
  };

  const passesSearchAndFilters = (product) => {
    const searchPass =
      !searchTerm ||
      fields.some((field) => {
        const val = product[field];
        return val != null && norm(val).includes(norm(searchTerm));
      });

    if (!searchPass) return false;

    const categoryPass =
      !quickCategory ||
      norm(product?.category || product?.catageory).includes(
        norm(quickCategory),
      );

    if (!categoryPass) return false;

    const subCategoryPass =
      !quickSubCategory ||
      norm(
        product?.subCategory ||
          product?.subcategory ||
          product?.subCatageory ||
          product?.subcatageory ||
          product?.sub_catageory,
      ).includes(norm(quickSubCategory));

    if (!subCategoryPass) return false;

    const namePass =
      !quickName || norm(product?.name).includes(norm(quickName));

    if (!namePass) return false;

    const fieldFilterPass = (filterRules || []).every((rule) =>
      applyFieldFilterRule(product, rule),
    );

    if (!fieldFilterPass) return false;

    return applySmartFilter(product, smartFilter);
  };

  const sortedProducts = useMemo(() => {
    const arr = [...displayProducts].filter(passesSearchAndFilters);

    arr.sort((a, b) => {
      const aMatch = matchesSearch(a);
      const bMatch = matchesSearch(b);

      if (searchTerm) {
        if (aMatch !== bMatch) return aMatch ? -1 : 1;
      }

      const ac = norm(a.category);
      const bc = norm(b.category);
      if (ac !== bc) return ac.localeCompare(bc);

      const asc = norm(a.subCategory);
      const bsc = norm(b.subCategory);
      if (asc !== bsc) return asc.localeCompare(bsc);

      const aq = norm(a.boardQuality);
      const bq = norm(b.boardQuality);
      if (aq !== bq) return aq.localeCompare(bq);

      const an = norm(a.name);
      const bn = norm(b.name);

      return an.localeCompare(bn);
    });

    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    displayProducts,
    searchTerm,
    quickCategory,
    quickSubCategory,
    quickName,
    filterRules,
    smartFilter,
  ]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const deleteSelected = async () => {
    await axios.delete(API_URL, { data: { ids: selectedIds } });

    setSelectedIds([]);

    if (expandedId && selectedIds.includes(expandedId)) {
      setExpandedId(null);
      setMode("view");
      setFormData({});
      setOriginalData({});
    }

    fetchData();
  };

  const openCard = (product) => {
    const id = product?._id;

    if (expandedId === id && mode === "view") {
      setExpandedId(null);
      setFormData({});
      setOriginalData({});
      return;
    }

    const final = calculateFields(product);

    setExpandedId(id);
    setMode("view");
    setFormData(final);
    setOriginalData(final);
  };

  const goToSecondWithProducts = (productsArr) => {
    const clean = (productsArr || []).filter(Boolean);
    if (!clean.length) return;

    const withAuto = clean.map((p) => calculateFields(p));

    try {
      sessionStorage.setItem("createOrder_products", JSON.stringify(withAuto));
    } catch {}

    navigate("/second", { state: { products: withAuto } });
  };

  const handleCreateOrderSelected = () => {
    const selectedProducts = sortedProducts.filter((p) =>
      selectedIds.includes(p._id),
    );

    goToSecondWithProducts(selectedProducts);
  };

  const handleCreateOrderSingle = (product) => {
    goToSecondWithProducts([product]);
  };

  const startEdit = () => setMode("edit");

  const cancelEdit = () => {
    setMode("view");
    setFormData(originalData || {});
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => applyFieldChange(prev, field, value));
  };

  const saveUpdate = async () => {
    if (!expandedId) return;

    const finalData = calculateFields(formData);
    const payload = normalizePayloadForApi(finalData);

    await axios.put(`${API_URL}/${expandedId}`, payload);

    saveLastProductToLS(finalData);
    addSuggestionsFromProduct(finalData);

    setMode("view");
    setOriginalData(finalData);

    fetchData();
  };

  const openedProduct = useMemo(
    () => sortedProducts.find((p) => p?._id === expandedId) || null,
    [sortedProducts, expandedId],
  );

  const downloadExcelAll = () => {
    const rows = (sortedProducts || []).map((p) => {
      const row = { _id: p._id || "" };

      for (const k of fields) row[k] = p?.[k] ?? "";

      return row;
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "Products");
    XLSX.writeFile(wb, "products.xlsx");
  };

  const downloadTemplate = () => {
    const row = { _id: "" };

    for (const k of fields) row[k] = "";

    const ws = XLSX.utils.json_to_sheet([row]);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "products_template.xlsx");
  };

  const openUploadPicker = () => {
    if (bulkLoading) return;

    if (fileInputRef.current) fileInputRef.current.click();
  };

  const buildLookupKey = (obj) => {
    const n = norm(obj?.name);
    const b = norm(obj?.boardQuality);
    const c = norm(obj?.category);
    const s = norm(obj?.subCategory);

    return `${n}__${b}__${c}__${s}`;
  };

  const handleBulkFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";

    if (!file) return;

    setBulkLoading(true);
    setBulkReport({ created: [], updated: [], failed: [] });

    try {
      const byId = new Map((products || []).map((p) => [String(p._id), p]));
      const byKey = new Map(
        (products || []).map((p) => [buildLookupKey(p), p]),
      );

      const buf = await new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;

        reader.readAsArrayBuffer(file);
      });

      const wb = XLSX.read(buf, { type: "array" });
      const sheetName = wb.SheetNames?.[0];
      const ws = wb.Sheets?.[sheetName];

      const rawRows = XLSX.utils.sheet_to_json(ws, {
        defval: "",
        raw: false,
      });

      const fieldNormMap = {};

      for (const k of fields) fieldNormMap[headerKeyNorm(k)] = k;

      fieldNormMap["catageory"] = "category";
      fieldNormMap["catagory"] = "category";
      fieldNormMap["subcategory"] = "subCategory";
      fieldNormMap["subcatageory"] = "subCategory";
      fieldNormMap["subcatagory"] = "subCategory";
      fieldNormMap["subcat"] = "subCategory";

      fieldNormMap["gstrate"] = "gstAmount";
      fieldNormMap["printingper1000"] = "printingCharges";
      fieldNormMap["cuttingper1000"] = "cuttingCharges";
      fieldNormMap["printingcharges"] = "printingCharges";
      fieldNormMap["cuttingcharges"] = "cuttingCharges";
      fieldNormMap["pricepiece"] = "pricePerPiece";
      fieldNormMap["priceperpiece"] = "pricePerPiece";
      fieldNormMap["profitpiece"] = "profitPerPiece";
      fieldNormMap["profitperpiece"] = "profitPerPiece";

      const created = [];
      const updated = [];
      const failed = [];

      for (let i = 0; i < rawRows.length; i++) {
        const r = rawRows[i] || {};

        try {
          const rid =
            r._id || r.id || r.productId || r.productID || r.product_id || "";

          const patch = {};

          for (const [hk, hv] of Object.entries(r)) {
            const nk = headerKeyNorm(hk);

            if (!nk) continue;
            if (nk === "id" || nk === "_id" || nk === "productid") continue;

            const realKey = fieldNormMap[nk];

            if (!realKey) continue;
            if (hv === "" || hv == null) continue;

            if (FORCE_STRING_FIELDS.has(realKey)) {
              patch[realKey] = String(hv);
              continue;
            }

            const n = toNum(hv);

            patch[realKey] = n == null ? hv : n;
          }

          const normalizedPatch = normalizeProductFromApi(patch);

          let target = null;
          let targetId = "";

          if (rid) {
            targetId = String(rid).trim();
            target = byId.get(targetId) || null;
          } else {
            const key = buildLookupKey(normalizedPatch);

            target = byKey.get(key) || null;
            targetId = target?._id ? String(target._id) : "";
          }

          if (targetId && target) {
            const merged = normalizeProductFromApi({
              ...target,
              ...normalizedPatch,
            });

            const finalData = calculateFields(merged);
            const payload = normalizePayloadForApi(finalData);

            await axios.put(`${API_URL}/${targetId}`, payload);

            updated.push({
              row: i + 2,
              _id: targetId,
              name: String(finalData?.name || target?.name || ""),
            });
          } else {
            const finalData = calculateFields(normalizedPatch);
            const payload = normalizePayloadForApi(finalData);

            const res = await axios.post(API_URL, payload);
            const newDoc = res.data || null;

            created.push({
              row: i + 2,
              _id: newDoc?._id || "",
              name: String(finalData?.name || ""),
            });
          }
        } catch (rowErr) {
          failed.push({
            row: i + 2,
            error: rowErr?.message || String(rowErr),
          });
        }
      }

      setBulkReport({ created, updated, failed });
      setBulkReportOpen(true);

      await fetchData();
    } catch (err) {
      setBulkReport({
        created: [],
        updated: [],
        failed: [{ row: "-", error: err?.message || String(err) }],
      });

      setBulkReportOpen(true);
    } finally {
      setBulkLoading(false);
    }
  };

  const openCreate = () => {
    const last = loadLS(LS_LAST_PRODUCT_KEY, null);
    const base = last && typeof last === "object" ? { ...last } : {};

    delete base._id;
    delete base.__v;

    const init = calculateFields(base);

    setCreateForm(init);
    saveLastProductToLS(init);
    setCreateOpen(true);
  };

  const handleCreateFieldChange = (field, value) => {
    setCreateForm((prev) => {
      const next = applyFieldChange(prev, field, value);

      saveLastProductToLS(next);

      return next;
    });
  };

  const saveCreate = async () => {
    setCreateSaving(true);

    try {
      const finalData = calculateFields(createForm);
      const payload = normalizePayloadForApi(finalData);

      await axios.post(API_URL, payload);

      saveLastProductToLS(finalData);
      addSuggestionsFromProduct(finalData);

      setCreateOpen(false);
      setCreateForm({});

      await fetchData();
    } catch (e) {
      alert(
        e?.response?.data?.message || e?.message || "Failed to create product",
      );
    } finally {
      setCreateSaving(false);
    }
  };

  const createOrderSheetGsmOptions = useMemo(() => {
    return getOrderSheetGsmOptions(createForm?.boardQuality || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createForm?.boardQuality, boardCfgs]);

  return (
    <>
      <CssBaseline />

      <Box sx={{ minHeight: "100vh", background: UI.pageBg }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: "none" }}
          onChange={handleBulkFilePicked}
        />

        <Box sx={{ background: UI.headerBg, color: "white" }}>
          <Container maxWidth="xl">
            <Box
              sx={{
                py: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1.2,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                <Typography sx={{ fontWeight: 900, fontSize: 20 }}>
                  Rupioo
                </Typography>

                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: 16,
                    color: UI.amazonOrange,
                  }}
                >
                  Costing
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <FormControl
                  size="small"
                  sx={{
                    width: { xs: "100%", sm: 170 },
                    background: "#fff",
                    borderRadius: 2,
                    "& .MuiOutlinedInput-root": {
                      height: 40,
                      borderRadius: 2,
                      fontWeight: 900,
                    },
                    "& label": {
                      fontWeight: 900,
                    },
                  }}
                >
                  <InputLabel>Category</InputLabel>
                  <Select
                    label="Category"
                    value={quickCategory}
                    onChange={(e) => setQuickCategory(e.target.value)}
                  >
                    <MenuItem value="">All Category</MenuItem>

                    {quickCategoryOptions.map((x) => (
                      <MenuItem key={x} value={x}>
                        {x}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Autocomplete
                  freeSolo
                  size="small"
                  options={quickSubCategoryOptions}
                  inputValue={quickSubCategory}
                  onInputChange={(_, newVal) => setQuickSubCategory(newVal)}
                  sx={{
                    width: { xs: "100%", sm: 180 },
                    background: "#fff",
                    borderRadius: 2,
                    "& .MuiOutlinedInput-root": {
                      height: 40,
                      borderRadius: 2,
                      fontWeight: 900,
                    },
                    "& input": {
                      fontWeight: 800,
                      fontSize: 13,
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sub Category"
                      placeholder="Printed"
                    />
                  )}
                />

                <TextField
                  label="Name"
                  placeholder="8x8x5"
                  variant="outlined"
                  size="small"
                  value={quickName}
                  onChange={(e) => setQuickName(e.target.value)}
                  sx={{
                    width: { xs: "100%", sm: 170 },
                    background: "#fff",
                    borderRadius: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      height: 40,
                    },
                    "& input": {
                      fontWeight: 800,
                      fontSize: 13,
                    },
                    "& label": {
                      fontWeight: 900,
                    },
                  }}
                />

                <TextField
                  placeholder="Search..."
                  variant="outlined"
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{
                    width: { xs: "100%", sm: 170 },
                    background: "#fff",
                    borderRadius: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      height: 40,
                    },
                    "& input": {
                      fontWeight: 800,
                      fontSize: 13,
                    },
                  }}
                />
              </Box>

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  onClick={clearAllFilters}
                  sx={{
                    background: "#FFFFFF",
                    color: "#111",
                    fontWeight: 900,
                    "&:hover": { background: "#F0F2F2" },
                  }}
                >
                  Clear
                </Button>

                <Button
                  variant="contained"
                  startIcon={<FilterAltIcon />}
                  onClick={() => setFilterDialogOpen(true)}
                  sx={{
                    background: activeFilterCount > 0 ? "#7C3AED" : "#FFFFFF",
                    color: activeFilterCount > 0 ? "#fff" : "#111",
                    fontWeight: 900,
                    "&:hover": {
                      background: activeFilterCount > 0 ? "#6D28D9" : "#F0F2F2",
                    },
                  }}
                >
                  Filters{" "}
                  {activeFilterCount > 0 ? `(${activeFilterCount})` : ""}
                </Button>

                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openCreate}
                  sx={{
                    background: UI.amazonOrangeDeep,
                    color: "#111",
                    fontWeight: 900,
                    "&:hover": { background: UI.amazonOrange },
                  }}
                >
                  Add Product
                </Button>

                <Button
                  variant="contained"
                  startIcon={<SettingsIcon />}
                  onClick={() => setBoardDialogOpen(true)}
                  sx={{
                    background: "#3B82F6",
                    color: "#fff",
                    fontWeight: 900,
                    "&:hover": { background: "#2563EB" },
                  }}
                >
                  Board Setup
                </Button>

                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={downloadExcelAll}
                  sx={{
                    background: "#146EB4",
                    color: "#fff",
                    fontWeight: 900,
                    "&:hover": { background: "#0f5c9a" },
                  }}
                >
                  Download Excel
                </Button>

                <Button
                  variant="contained"
                  startIcon={<DescriptionIcon />}
                  onClick={downloadTemplate}
                  sx={{
                    background: "#2E86C1",
                    color: "#fff",
                    fontWeight: 900,
                    "&:hover": { background: "#2473a6" },
                  }}
                >
                  Template
                </Button>

                <Button
                  variant="contained"
                  startIcon={<UploadFileIcon />}
                  onClick={openUploadPicker}
                  disabled={bulkLoading}
                  sx={{
                    background: "#007600",
                    color: "#fff",
                    fontWeight: 900,
                    "&:hover": { background: "#005f00" },
                  }}
                >
                  {bulkLoading ? "Uploading..." : "Bulk Upload"}
                </Button>

                <Button
                  variant="contained"
                  startIcon={<ShoppingCartCheckoutIcon />}
                  disabled={selectedIds.length === 0}
                  onClick={handleCreateOrderSelected}
                  sx={{
                    background: UI.amazonOrangeDeep,
                    color: "#111",
                    fontWeight: 900,
                    "&:hover": { background: UI.amazonOrange },
                  }}
                >
                  Create Order ({selectedIds.length})
                </Button>

                {selectedIds.length > 0 && (
                  <Button
                    variant="contained"
                    onClick={deleteSelected}
                    sx={{
                      background: "#B12704",
                      fontWeight: 900,
                      "&:hover": { background: "#8f1f03" },
                    }}
                  >
                    Delete ({selectedIds.length})
                  </Button>
                )}
              </Box>
            </Box>
          </Container>
        </Box>

        <Container maxWidth={false} sx={{ px: { xs: 2, md: 3 } }}>
          <Box sx={{ py: 2.5 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              {sortedProducts.map((product) => {
                const isMatch = matchesSearch(product);
                const isSelected = selectedIds.includes(product._id);
                const isOpen = expandedId === product._id;

                return (
                  <React.Fragment key={product._id}>
                    <Box
                      sx={{
                        flex: "0 0 calc(16.666% - 16px)",
                        maxWidth: "calc(16.666% - 16px)",
                        minWidth: 200,

                        "@media (max-width: 1500px)": {
                          flex: "0 0 calc(20% - 16px)",
                          maxWidth: "calc(20% - 16px)",
                        },

                        "@media (max-width: 1300px)": {
                          flex: "0 0 calc(25% - 16px)",
                          maxWidth: "calc(25% - 16px)",
                        },

                        "@media (max-width: 1000px)": {
                          flex: "0 0 calc(33.333% - 16px)",
                          maxWidth: "calc(33.333% - 16px)",
                        },

                        "@media (max-width: 760px)": {
                          flex: "0 0 calc(50% - 16px)",
                          maxWidth: "calc(50% - 16px)",
                        },

                        "@media (max-width: 520px)": {
                          flex: "0 0 100%",
                          maxWidth: "100%",
                        },
                      }}
                    >
                      <ProductCard
                        product={product}
                        isOpen={isOpen}
                        isMatch={isMatch}
                        isSelected={isSelected}
                        onToggleSelect={toggleSelect}
                        onOpen={openCard}
                        onCreateOrderSingle={handleCreateOrderSingle}
                      />
                    </Box>

                    {isOpen && openedProduct && (
                      <Box sx={{ flex: "0 0 100%", maxWidth: "100%" }}>
                        <DetailsPanel
                          product={openedProduct}
                          mode={mode}
                          formData={formData}
                          onStartEdit={startEdit}
                          onSaveUpdate={saveUpdate}
                          onCancelEdit={cancelEdit}
                          onClose={() => {
                            setExpandedId(null);
                            setMode("view");
                            setFormData({});
                            setOriginalData({});
                          }}
                          onChangeField={handleFieldChange}
                          boardQualityOptions={(() => {
                            const cur = safeStr(
                              (mode === "edit"
                                ? formData?.boardQuality
                                : openedProduct?.boardQuality) || "",
                            ).trim();

                            if (cur && !boardQualityOptions.includes(cur)) {
                              return [cur, ...boardQualityOptions];
                            }

                            return boardQualityOptions;
                          })()}
                          orderSheetGsmOptions={getOrderSheetGsmOptions(
                            (mode === "edit"
                              ? formData?.boardQuality
                              : openedProduct?.boardQuality) || "",
                          )}
                          boxWidth={editorBoxWidth}
                          setBoxWidth={setEditorBoxWidth}
                        />
                      </Box>
                    )}
                  </React.Fragment>
                );
              })}
            </Box>
          </Box>
        </Container>

        <Dialog
          open={bulkReportOpen}
          onClose={() => setBulkReportOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 1000 }}>
            Bulk Upload Report
          </DialogTitle>

          <DialogContent dividers>
            <Typography sx={{ fontWeight: 900, mb: 1 }}>
              Created: {bulkReport.created.length} | Updated:{" "}
              {bulkReport.updated.length} | Failed: {bulkReport.failed.length}
            </Typography>

            <Divider sx={{ my: 1.5 }} />

            {bulkReport.created.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 900, mb: 0.7 }}>
                  ✅ Created
                </Typography>

                {bulkReport.created.slice(0, 50).map((x, idx) => (
                  <Typography key={idx} sx={{ color: UI.sub, fontSize: 13 }}>
                    Row {x.row}: {x.name} ({x._id})
                  </Typography>
                ))}
              </Box>
            )}

            {bulkReport.updated.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 900, mb: 0.7 }}>
                  🔁 Updated
                </Typography>

                {bulkReport.updated.slice(0, 50).map((x, idx) => (
                  <Typography key={idx} sx={{ color: UI.sub, fontSize: 13 }}>
                    Row {x.row}: {x.name} ({x._id})
                  </Typography>
                ))}
              </Box>
            )}

            {bulkReport.failed.length > 0 && (
              <Box>
                <Typography sx={{ fontWeight: 900, mb: 0.7, color: "#B12704" }}>
                  ❌ Failed
                </Typography>

                {bulkReport.failed.slice(0, 80).map((x, idx) => (
                  <Typography key={idx} sx={{ color: "#B12704", fontSize: 13 }}>
                    Row {x.row}: {x.error}
                  </Typography>
                ))}
              </Box>
            )}
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => setBulkReportOpen(false)}
              startIcon={<CloseIcon />}
              sx={{ fontWeight: 900 }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <AdvancedFilterDialog
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          filterRules={filterRules}
          setFilterRules={setFilterRules}
          smartFilter={smartFilter}
          setSmartFilter={setSmartFilter}
          onClearAll={clearAllFilters}
        />

        <BoardSetupDialog
          open={boardDialogOpen}
          onClose={() => setBoardDialogOpen(false)}
          boardCfgs={boardCfgs}
          onSaved={fetchBoardCfgs}
        />

        <CreateProductDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          formData={createForm}
          onChangeField={handleCreateFieldChange}
          onSave={saveCreate}
          saving={createSaving}
          boardQualityOptions={boardQualityOptions}
          orderSheetGsmOptions={createOrderSheetGsmOptions}
          suggestions={suggestions}
          enableSuggest={true}
          boxWidth={editorBoxWidth}
          setBoxWidth={setEditorBoxWidth}
        />
      </Box>
    </>
  );
}
